import React from "react";
import classNames from "classnames";
import { CellContext } from "@tanstack/react-table";
import { RowExpansion } from "@design-system/ViewTable/RowExpansion";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { isGroupEndPeriod, isNLevelClassReport } from "@common/helpers/reports";
import { ReportPeriodWithLabel } from "@common/types/reports/types";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { useViewTableMeta } from "@hooks/view/useViewTableMeta";
import { ProfitLossViewValueCell } from "./ProfitLossViewValueCell";
import { getProfitLossCellClassName } from "./profitLossCellClassName";
import { PLTableCallbacks } from "./profitLossDrillDown.lib";
import { ProfitLossViewRow } from "./profitLossViewTypes";
import styles from "./ProfitLossTable.module.scss";

interface MakePeriodCellOptions {
    period: ReportPeriodWithLabel;
    periodIndex: number;
    nonTotalPeriods: ReportPeriodWithLabel[];
    nonTotalIndex: number;
}

export const makeProfitLossPeriodCell = ({
    period,
    periodIndex,
    nonTotalPeriods,
    nonTotalIndex,
}: MakePeriodCellOptions) => {
    const isTotalPeriod = Boolean(period.isTotal);
    // N-level draws full-height separators at the table level, making the legacy
    // per-cell group-end divider redundant.
    const isNLevelClassMode = isNLevelClassReport(nonTotalPeriods);
    const isGroupEnd =
        !isTotalPeriod &&
        !isNLevelClassMode &&
        isGroupEndPeriod(nonTotalPeriods, nonTotalIndex);

    const ProfitLossPeriodCell = ({
        row,
        table,
    }: CellContext<
        ViewTableFeatures,
        ProfitLossViewRow,
        unknown
    >): React.ReactElement => {
        const { values, accountCodes, isTotal, isSummary, counterpartyId } =
            row.original;
        const onDrillDown = useViewTableMeta<PLTableCallbacks, unknown>(table)
            .callbacks?.onDrillDown;
        // Counterparty rows never expand further, so their values are always
        // shown; section/category rows hide values when expanded.
        const isCounterparty = counterpartyId !== undefined;
        const value = values[periodIndex]?.value ?? 0;

        return (
            <RowExpansion row={row}>
                {(isExpanded) => {
                    const shouldShowValues =
                        isCounterparty || !row.getCanExpand() || !isExpanded;
                    return shouldShowValues && onDrillDown ? (
                        <ProfitLossViewValueCell
                            value={value}
                            accountCodes={
                                isTotalPeriod && period.comparisonDiff
                                    ? undefined
                                    : accountCodes
                            }
                            period={period}
                            isGroupEnd={isGroupEnd}
                            isTotal={isTotal}
                            isSummary={isSummary}
                            counterpartyId={counterpartyId}
                            onDrillDown={onDrillDown}
                        />
                    ) : (
                        <ViewTableCell
                            className={classNames(
                                styles.numericCell,
                                getProfitLossCellClassName(isTotal, isSummary),
                            )}
                        />
                    );
                }}
            </RowExpansion>
        );
    };

    return ProfitLossPeriodCell;
};
